import { Decoder } from './Coding.ts'
import { mat4, Ray, vec2, vec3, vec4 } from './VectorMath.ts'

const π = Math.PI
const half_π = π * 0.5
const twice_π = 2.0 * π

function euclideanRemainder(x: number, y: number): number {
  return x - y * Math.floor(x / y)
}

function trimmedAltitude(altitude: number): number {
  return Math.max(-half_π, Math.min(altitude, half_π))
}

function trimmedAzimuth(azimuth: number): number {
  return euclideanRemainder(azimuth, twice_π)
}

/// An immutable value type representing a camera's pose: its position, orientation, and field of view.
export class Camera {
  // https://en.wikipedia.org/wiki/Spherical_coordinate_system

  /** Position in world space. */
  readonly position: vec3

  /** Altitude (angle from x/z plane) in world space. Radians. -π/2 ≤ `altitude` ≤ π/2. */
  readonly altitude: number

  /** Azimuth (angle from -z axis) in world space. Radians. 0 ≤ `azimuth` ≤ 2π. This is measured from the -z axis because the OpenGL camera is considered to point down the -z axis. */
  readonly azimuth: number

  /** Viewport width / height. */
  readonly aspectRatio: number

  /** Viewport field-of-view along the vertical axis. Radians. */
  readonly verticalFieldOfView: number

  /** Position on the z axis of the near clip plane. Must be a negative number. Defaults to `mat4.defaultNear`.  */
  readonly near: number

  constructor(props: {
    position: vec3
    altitude: number
    azimuth: number
    aspectRatio: number // viewport width / height
    fov?: number
    near?: number
  }) {
    this.position = props.position
    this.altitude = trimmedAltitude(props.altitude)
    this.azimuth = trimmedAzimuth(props.azimuth)
    this.aspectRatio = props.aspectRatio
    this.verticalFieldOfView = props.fov ?? 0.25 * π
    this.near = props.near ?? mat4.defaultNear
  }

  static decodeFrom(decoder: Decoder): Camera {
    const container = decoder.keyedDecoder()
    return new Camera({
      position: container.decodeInstance('position', vec3.decodeFrom),
      altitude: container.decodeNumber('altitude', 0),
      azimuth: container.decodeNumber('azimuth', 0),
      aspectRatio: container.decodeNumber('aspectRatio', 1.0),
      fov: container.decodeNumber('fov', 0.25 * π),
      near: container.decodeNumber('near', mat4.defaultNear
      ),
    })
  }

  static readonly zero: Camera = new Camera({
    position: vec3.zero,
    altitude: 0,
    azimuth: 0,
    aspectRatio: 1
  })

  static readonly fallback: Camera = Camera.zero
    .translatedInViewSpace(new vec3(2, 2, -6))
    .lookingAt(vec3.zero)

  /**
   * @param offset An offset in **view space** by which to move the camera.
   * @returns A new `Camera`.
   */
  translatedInViewSpace(offset: vec3): Camera {
    return this.translatedInWorldSpace(this.worldFromView.basis.times(offset))
  }

  /**
   * @param offset An offset in **world space** by which to move the camera.
   * @returns A new `Camera`.
   */
  translatedInWorldSpace(offset: vec3): Camera {
    return new Camera({
      ...this,
      position: this.position.plus(offset)
    })
  }

  /**
   * Adjust my `azimuth` and `altitude` by the given amounts, and adjust my `position` to compensate so that `around` stays at the same point in my view space.
   * 
   * If `around` has any infinite component, then I adjust my `azimuth` and `altitude` without modifying my `position`.
   * 
   * @param dAzimuth The amount to add to my `azimuth`.
   * @param dAltitude The amount to add to my `altitude`.
   * @param around A point in world space that should remain at the same position in my view space.
   * @returns An adjusted `Camera`.
   */
  orbited(props: {
    dAzimuth: number
    dAltitude: number
    around: vec3
  }): Camera {
    let { around, dAzimuth, dAltitude } = props
    let center = around.asPoint
    if (!isFinite(center.lengthSquared)) {
      center = this.position.asPoint
      dAzimuth = -dAzimuth
      dAltitude = -dAltitude
    }

    const alNew = trimmedAltitude(this.altitude + dAltitude)
    const azNew = trimmedAzimuth(this.azimuth + dAzimuth)

    const A = this.viewFromWorld
      .rotated(vec3.xPlus, alNew)
      .rotated(vec3.yPlus, azNew)

    const pNew = center.minus(A.times(center)).xyz

    return new Camera({
      ...this,
      position: pNew,
      azimuth: azNew,
      altitude: alNew,
    })
  }

  /**
   * @param dx The amount by which I move my `position` along the x axis of my view space.
   * @param dy The amount by which I move my `position` along the y axis of my view space.
   * @param dz The amount by which I move my `position` along the z axis of my view space.
   * @returns An adjusted `Camera`.
   */
  panned(dx: number, dy: number, dz: number): Camera {
    const dp = this.worldFromView.basis.times(new vec3(dx, dy, dz))
    return new Camera({
      ...this,
      position: this.position.plus(dp),
    })
  }

  lookingAt(target: vec3): Camera {
    const { x, y, z } = target.minus(this.position)
    const r = Math.hypot(x, y, z)
    return r < 1e-9 ? this : new Camera({
      ...this,
      azimuth: Math.atan2(-x, -z),
      altitude: Math.asin(y / r),
    })
  }

  get viewFromWorld(): mat4 {
    return mat4.translation(this.position.negated)
      .rotated(vec3.yPlus, -this.azimuth)
      .rotated(vec3.xPlus, -this.altitude)
  }

  get worldFromView(): mat4 {
    return mat4.rotation(vec3.xPlus, this.altitude)
      .rotated(vec3.yPlus, this.azimuth)
      .translated(this.position)
  }

  get clipFromView(): mat4 {
    return mat4.perspective({
      verticalFieldOfView: this.verticalFieldOfView,
      aspectRatio: this.aspectRatio,
      near: this.near,
    })
  }

  get viewFromClip(): mat4 {
    return mat4.inversePerspective({
      verticalFieldOfView: this.verticalFieldOfView,
      aspectRatio: this.aspectRatio,
      near: this.near,
    })
  }

  /// Find the ray in world space that passes through all points that with the given normalized device coordinates.
  worldRayForNdc(ndc: vec2): Ray {
    // This calculation is based on the use of reverse Z and infinite far plane.

    const negativeNear = -this.near

    // Perspective un-divide, assuming we want to get a point that is "on the screen" where the view volume extends back from the screen surface.
    const xc = negativeNear * ndc.x
    const yc = negativeNear * ndc.y

    // The ray under the mouse, directly down the view volume, starts at the near plane.
    const r0 = this.worldFromView
      .times(this.viewFromClip)
      .times(new vec4(xc, yc, negativeNear, negativeNear))
      .xyz

    const rd = this.worldFromView
      .times(this.viewFromClip)
      .times(new vec4(xc, yc, 0, negativeNear))
      .xyz

    return new Ray(r0, rd)
  }
}

if (import.meta.vitest) {
  const { describe, it, expect } = import.meta.vitest

  describe('Camera', () => {
    describe('lookingAt', () => {
      for (let x = -1; x <= 1; x += 0.5) {
        for (let y = -1; y <= 1; y += 0.5) {
          for (let z = -1; z <= 1; z += 0.5) {
            if (x === 0 && y === 0 && z === 0) {
              continue
            }
            const target = new vec3(x, y, z)
            it(`can look at ${Array.from(target)}`, () => {
              const camera = Camera.zero.lookingAt(target)
              const p = camera.viewFromWorld.times(new vec4(...target.tuple, 1))
              expect.soft(Array.from(p.xyz)).toEqual([
                expect.closeTo(0, 7),
                expect.closeTo(0, 7),
                expect.closeTo(-target.length, 7),
              ])
            })
          }
        }
      }
    })

    describe('viewFromWorld', () => {
      function check(x: number, y: number, z: number, al: number, az: number) {
        const camera = new Camera({
          position: new vec3(x, y, z),
          altitude: al,
          azimuth: az,
          aspectRatio: 1
        })

        const viewFromWorld = camera.viewFromWorld
        const worldFromView = camera.worldFromView
        const actual = viewFromWorld.times(worldFromView)
        const expected = mat4.identity

        expect(Array.from(actual)).toEqual(Array.from(expected).map(v => expect.closeTo(v, 7)))
      }

      it('inverts worldFromView', () => {
        for (let x = -1; x <= 1; x += 0.5) {
          for (let y = -1; y <= 1; y += 0.5) {
            for (let z = -1; z <= 1; z += 0.5) {
              for (let al = -1; al <= 1; al += 0.5) {
                for (let az = -1; az <= 1; az += 0.5) {
                  check(x, y, z, al, az)
                }
              }
            }
          }
        }
      })
    })
  })
}
